'use client'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import {
    MdAdd,
    MdArrowBack,
    MdDelete,
    MdInventory2,
    MdPointOfSale,
    MdSearch,
} from 'react-icons/md'

import { ClientSelector } from '@/components/client/ClientSelector'
import PaymentOptions from '@/components/billing/payment-options'
import { PaymentQuoteOption } from '@/lib/api/financial/financial-billing-api'
import {
    CounterSaleLine,
    CounterSalePaymentMethod,
    CounterSaleLineInput,
    CounterSaleProduct,
    createCounterSale,
    quoteCounterSale,
    searchCounterSaleProducts,
} from '@/lib/api/commerce/counter-sale'
import {
    currencyFormat,
    parseCurrencyToCents,
} from '@/lib/api/catalog/catalog-price-format'
import { useAuth } from '@/store/hooks'
import { localTodayInput } from '@/lib/utils/date-input'

/**
 * VENDA DE BALCÃO: mercadoria vendida no painel, sem passar pela loja.
 *
 * ⚠️ **Esta tela NUNCA soma.** Todo valor exibido — preço unitário, total da linha,
 * desconto e total da venda — vem do `POST /counter-sales/quote`. Quando os dois lados
 * somam, o operador lê um número e a cobrança cobra outro; foi exatamente o que aconteceu
 * no checkout da loja antes de o cálculo virar responsabilidade única do servidor.
 *
 * O desconto é limitado pelo teto do item no catálogo (`maxDiscount`). Item sem teto
 * cadastrado não aceita desconto nenhum — é a regra da plataforma desde 2026-08-06, e o
 * servidor recusa; aqui o piso é mostrado ANTES de o operador tentar.
 */

/** Como o total é anunciado depois que a forma foi escolhida. */
const METHOD_TOTAL_LABEL: Record<string, string> = {
    PIX: 'no Pix',
    CREDIT_CARD: 'no crédito',
    DEBIT_CARD: 'no débito',
    BOLETO: 'no boleto',
}

/** Linha em edição na tela. O preço definitivo é sempre o que o quote devolveu. */
interface CartLine {
    catalogId: number
    name: string
    quantity: number
    /** Desconto digitado como VALOR unitário (em texto, formatado em reais). */
    priceInput: string
}

export default function CounterSalePage() {
    const navigate = useNavigate()
    const { user } = useAuth()
    const [searchParams] = useSearchParams()

    const [clientId, setClientId] = useState<number | null>(null)
    const [cart, setCart] = useState<CartLine[]>([])
    /**
     * Forma escolhida no `PaymentOptions`. Vazio = "decidir na tela de pagamento", que é
     * por onde passam dinheiro, maquininha, "outro" e o split entre formas.
     */
    const [paymentMethod, setPaymentMethod] = useState<string>('')
    const [installmentCount, setInstallmentCount] = useState<number | null>(null)
    /**
     * A cotação da forma escolhida — é ela que diz o que o cliente paga.
     *
     * `total` do resumo é a BASE (mercadoria − desconto); a taxa do meio de pagamento entra
     * por cima, e é o `PaymentOptions` que a calcula. Mostrar a base como se fosse o valor
     * final é o defeito que o agendamento já corrigiu: o operador fechava "R$ 300 no Pix" e
     * o link chegava com outro número.
     */
    const [paymentQuote, setPaymentQuote] = useState<PaymentQuoteOption | null>(null)
    const [notes, setNotes] = useState('')
    /**
     * Dia em que a venda aconteceu. Hoje por padrão; um dia anterior quando o operador
     * registra depois. Vira o vencimento da cobrança — e, com dinheiro ou maquininha, a
     * data do pagamento.
     */
    const [saleDate, setSaleDate] = useState(localTodayInput)

    const [search, setSearch] = useState('')
    const [results, setResults] = useState<CounterSaleProduct[]>([])
    const [searching, setSearching] = useState(false)
    const [showResults, setShowResults] = useState(false)
    /** Variações do produto que veio do botão "Vender" do catálogo, para escolher uma. */
    const [variationChoices, setVariationChoices] = useState<CounterSaleProduct[]>([])
    const searchBoxRef = useRef<HTMLDivElement>(null)
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    const [quoteLines, setQuoteLines] = useState<CounterSaleLine[]>([])
    const [subtotal, setSubtotal] = useState(0)
    const [discount, setDiscount] = useState(0)
    const [total, setTotal] = useState(0)
    const [quoting, setQuoting] = useState(false)
    const [quoteError, setQuoteError] = useState<string | null>(null)
    const [selling, setSelling] = useState(false)

    const quoteById = useMemo(() => {
        const map = new Map<number, CounterSaleLine>()
        for (const line of quoteLines) map.set(line.catalogId, line)
        return map
    }, [quoteLines])

    // === Busca de produtos ===
    const runSearch = useCallback(
        async (term: string) => {
            if (!user?.companyId) return
            setSearching(true)
            try {
                // Busca própria do balcão: traz as variações (com o nome do produto) e
                // acha pelo código de barras delas. A do catálogo só devolvia o pai.
                const response = await searchCounterSaleProducts(
                    term.trim() || undefined
                )
                if (response.success) {
                    setResults(response.result ?? [])
                }
            } finally {
                setSearching(false)
            }
        },
        [user?.companyId]
    )

    useEffect(() => {
        if (!showResults) return
        if (debounceRef.current) clearTimeout(debounceRef.current)
        debounceRef.current = setTimeout(() => runSearch(search), 300)
        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current)
        }
    }, [search, showResults, runSearch])

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                searchBoxRef.current &&
                !searchBoxRef.current.contains(event.target as Node)
            ) {
                setShowResults(false)
            }
        }
        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [])

    const addProduct = useCallback((product: { id: number; name: string }) => {
        setCart(previous => {
            // Mesma mercadoria duas vezes quebraria a reserva e o pedido (que são únicos
            // por item): a segunda leitura do código de barras soma a quantidade.
            const existing = previous.find(line => line.catalogId === product.id)
            if (existing) {
                return previous.map(line =>
                    line.catalogId === product.id
                        ? { ...line, quantity: line.quantity + 1 }
                        : line
                )
            }
            return [
                ...previous,
                {
                    catalogId: product.id,
                    name: product.name,
                    quantity: 1,
                    priceInput: '',
                },
            ]
        })
        setSearch('')
        setShowResults(false)
    }, [])

    // Produto vindo do botão "Vender" da listagem do catálogo. Produto com variações não se
    // vende pelo pai (o quote recusa): a tela mostra as variações para o operador escolher.
    useEffect(() => {
        const fromUrl = searchParams.get('catalogId')
        if (!fromUrl || !user?.companyId) return
        const catalogId = Number(fromUrl)
        if (!Number.isInteger(catalogId) || catalogId <= 0) return
        let cancelled = false
        searchCounterSaleProducts(undefined, catalogId).then(response => {
            if (cancelled || !response.success) return
            const items = response.result ?? []
            const own = items.find(item => item.id === catalogId)
            if (own) addProduct(own)
            else if (items.length > 0) setVariationChoices(items)
            else toast.error('Este produto não está disponível para venda.')
        })
        return () => {
            cancelled = true
        }
        // Só na montagem, com o id que veio da listagem.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.companyId])

    const updateLine = (catalogId: number, patch: Partial<CartLine>) => {
        setCart(previous =>
            previous.map(line =>
                line.catalogId === catalogId ? { ...line, ...patch } : line
            )
        )
    }

    const removeLine = (catalogId: number) => {
        setCart(previous => previous.filter(line => line.catalogId !== catalogId))
    }

    // === Resumo (sempre do servidor) ===
    const lineInputs: CounterSaleLineInput[] = useMemo(
        () =>
            cart.map(line => {
                const typed = parseCurrencyToCents(line.priceInput || '0')
                return {
                    catalogId: line.catalogId,
                    quantity: line.quantity,
                    ...(typed > 0 ? { unitPrice: typed } : {}),
                }
            }),
        [cart]
    )

    /**
     * O que o resumo observa: o CONTEÚDO das linhas, não a referência do array.
     *
     * ⚠️ Depender de `lineInputs` aqui produzia laço infinito. O effect grava o nome que o
     * servidor devolveu (`setCart`), e `previous.map()` devolve um array NOVO mesmo quando
     * nenhuma linha mudou — nova referência de `cart`, novo `lineInputs`, effect de novo,
     * sem fim. A chave serializada só muda quando produto, quantidade ou preço mudam de
     * verdade, e o `setCart` abaixo passou a devolver `previous` quando não há o que
     * alterar. As duas proteções ficam: uma sozinha volta a ser frágil ao próximo `setCart`
     * que alguém acrescente aqui.
     */
    const lineInputsKey = JSON.stringify(lineInputs)

    useEffect(() => {
        if (lineInputs.length === 0) {
            setQuoteLines([])
            setSubtotal(0)
            setDiscount(0)
            setTotal(0)
            setQuoteError(null)
            return
        }

        let cancelled = false
        const timer = setTimeout(async () => {
            setQuoting(true)
            try {
                const response = await quoteCounterSale(lineInputs)
                if (cancelled) return
                if (response.success && response.result) {
                    setQuoteLines(response.result.lines)
                    setSubtotal(response.result.subtotal)
                    setDiscount(response.result.discount)
                    setTotal(response.result.total)
                    setQuoteError(null)
                    // O nome definitivo vem do servidor: a linha aberta pela listagem
                    // nasce com rótulo provisório. Devolve `previous` INTACTO quando não há
                    // nome a corrigir — array novo aqui é re-render em cadeia.
                    setCart(previous => {
                        let changed = false
                        const next = previous.map(line => {
                            const quoted = response.result!.lines.find(
                                item => item.catalogId === line.catalogId
                            )
                            if (!quoted || line.name === quoted.name) return line
                            changed = true
                            return { ...line, name: quoted.name }
                        })
                        return changed ? next : previous
                    })
                } else {
                    setQuoteError(response.error || 'Não foi possível calcular a venda')
                    setTotal(0)
                }
            } finally {
                if (!cancelled) setQuoting(false)
            }
        }, 350)

        return () => {
            cancelled = true
            clearTimeout(timer)
        }
        // Observa o conteúdo (`lineInputsKey`), não a referência — ver a nota acima.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lineInputsKey])

    const missing = useMemo(() => {
        const pending: string[] = []
        if (!clientId) pending.push('o cliente')
        if (!saleDate || saleDate > localTodayInput())
            pending.push('uma data da venda que não seja futura')
        if (cart.length === 0) pending.push('ao menos um produto')
        if (quoteError) pending.push('corrigir o preço dos produtos')
        return pending
    }, [clientId, cart.length, quoteError, saleDate])

    /**
     * Fecha a venda e leva o operador ao lugar certo — o mesmo desvio do agendamento.
     *
     * Com forma do gateway escolhida, a cobrança nasce completa (QR pronto, parcelamento
     * travado) e não há mais nada a resolver: vai ao pedido. Sem forma, ela nasce em aberto
     * e o operador vai à cobrança, onde estão dinheiro, maquininha, "outro" e o split — e é
     * de lá que sai o link. Quem decide é o `paymentResolved` que o SERVIDOR devolveu, não
     * o que a tela julga ter enviado.
     */
    const handleSell = async () => {
        if (!clientId || cart.length === 0 || total <= 0) return
        setSelling(true)
        try {
            const response = await createCounterSale({
                clientId,
                lines: lineInputs,
                saleDate,
                paymentMethod:
                    (paymentMethod as CounterSalePaymentMethod) || undefined,
                // Só o cartão parcela; nas demais formas o número seria ruído no checkout.
                installmentCount:
                    paymentMethod === 'CREDIT_CARD' &&
                    (installmentCount ?? 1) > 1
                        ? (installmentCount as number)
                        : undefined,
                notes: notes.trim() || undefined,
            })
            if (!response.success || !response.result) {
                toast.error(response.error || 'Não foi possível concluir a venda')
                return
            }
            toast.success('Venda registrada')
            if (response.result.paymentResolved) {
                navigate(`/bookings/orders/${response.result.orderId}`)
                return
            }
            navigate(
                `/financial/billings/${response.result.billingId}/edit?from=counter-sale`
            )
        } finally {
            setSelling(false)
        }
    }

    return (
        <div className="flex flex-col gap-6">
            <div className="flex items-center gap-3">
                <button
                    className="btn btn-ghost btn-sm btn-circle"
                    onClick={() => navigate('/registrations/catalog?category=PRODUCT')}
                    title="Voltar para os produtos"
                >
                    <MdArrowBack className="text-xl" />
                </button>
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-primary/70 flex items-center justify-center shadow-lg">
                    <MdPointOfSale className="text-2xl text-white" />
                </div>
                <div>
                    <h1 className="sm:text-lg lg:text-2xl font-bold text-base-content">
                        Venda de produtos
                    </h1>
                    <p className="text-sm text-base-content/60">
                        Venda de balcão: gera a cobrança e reserva o estoque desta
                        unidade
                    </p>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* ===== Carrinho ===== */}
                <div className="lg:col-span-2 flex flex-col gap-6">
                    <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6">
                        <h2 className="text-lg font-semibold text-base-content mb-4">
                            Produtos
                        </h2>

                        <div className="relative" ref={searchBoxRef}>
                            <label className="label pb-1">
                                <span className="label-text font-medium">
                                    Buscar produto
                                </span>
                            </label>
                            <div className="relative">
                                <MdSearch className="absolute z-10 left-3 top-1/2 -translate-y-1/2 text-base-content/70 text-xl pointer-events-none" />
                                <input
                                    type="text"
                                    className="input input-bordered w-full pl-10"
                                    placeholder="Nome do produto..."
                                    value={search}
                                    onChange={event => {
                                        setSearch(event.target.value)
                                        setShowResults(true)
                                    }}
                                    onFocus={() => setShowResults(true)}
                                />
                            </div>

                            {showResults && (
                                <div className="absolute z-20 mt-1 w-full bg-base-100 border border-base-300 rounded-lg shadow-lg max-h-72 overflow-y-auto">
                                    {searching && (
                                        <div className="p-3 text-sm text-base-content/60">
                                            Buscando...
                                        </div>
                                    )}
                                    {!searching && results.length === 0 && (
                                        <div className="p-3 text-sm text-base-content/60">
                                            Nenhum produto encontrado
                                        </div>
                                    )}
                                    {results.map(product => (
                                        <button
                                            key={product.id}
                                            type="button"
                                            className="w-full text-left px-3 py-2 hover:bg-base-200 flex items-center justify-between gap-3"
                                            onClick={() => addProduct(product)}
                                        >
                                            <span className="min-w-0">
                                                <span className="block text-sm truncate">
                                                    {product.name}
                                                </span>
                                                <span className="block text-xs text-base-content/50">
                                                    {currencyFormat(product.price)}
                                                    {product.sku
                                                        ? ` · SKU ${product.sku}`
                                                        : ''}
                                                    {product.stockEnabled &&
                                                    product.stockQuantity != null
                                                        ? ` · Estoque: ${product.stockQuantity}`
                                                        : ''}
                                                </span>
                                            </span>
                                            <MdAdd className="text-lg text-primary shrink-0" />
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>

                        {variationChoices.length > 0 && (
                            <div className="mt-4 rounded-lg border border-primary/30 bg-primary/5 p-4">
                                <div className="flex items-center justify-between gap-3 mb-3">
                                    <p className="text-sm font-medium text-base-content">
                                        Este produto tem variações. Escolha a que vai vender:
                                    </p>
                                    <button
                                        type="button"
                                        className="btn btn-ghost btn-xs"
                                        onClick={() => setVariationChoices([])}
                                    >
                                        Fechar
                                    </button>
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    {variationChoices.map(product => (
                                        <button
                                            key={product.id}
                                            type="button"
                                            className="btn btn-sm btn-outline btn-primary h-auto py-2 flex-col items-start gap-0.5"
                                            onClick={() => {
                                                addProduct(product)
                                                setVariationChoices([])
                                            }}
                                        >
                                            <span className="text-sm normal-case">
                                                {product.name}
                                            </span>
                                            <span className="text-xs font-normal opacity-70">
                                                {currencyFormat(product.price)}
                                                {product.stockEnabled &&
                                                product.stockQuantity != null
                                                    ? ` · Estoque: ${product.stockQuantity}`
                                                    : ''}
                                            </span>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div className="mt-6">
                            {cart.length === 0 ? (
                                <div className="text-center py-10 text-base-content/60">
                                    <MdInventory2 className="text-4xl mx-auto mb-2 opacity-40" />
                                    <p className="text-sm">
                                        Nenhum produto na venda. Busque acima para
                                        adicionar.
                                    </p>
                                </div>
                            ) : (
                                <div className="overflow-x-auto">
                                    <table className="table table-sm">
                                        <thead>
                                            <tr>
                                                <th>Produto</th>
                                                <th className="w-24">Qtd.</th>
                                                <th className="w-40">
                                                    Valor unitário
                                                </th>
                                                <th className="w-32 text-right">
                                                    Total
                                                </th>
                                                <th className="w-12" />
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {cart.map(line => {
                                                const quoted = quoteById.get(
                                                    line.catalogId
                                                )
                                                return (
                                                    <tr key={line.catalogId}>
                                                        <td>
                                                            <div className="font-medium">
                                                                {line.name}
                                                            </div>
                                                            {quoted && (
                                                                <div className="text-xs text-base-content/60 mt-0.5 flex flex-wrap gap-x-3">
                                                                    <span>
                                                                        Tabela:{' '}
                                                                        {currencyFormat(
                                                                            quoted.basePrice
                                                                        )}
                                                                    </span>
                                                                    {quoted.maxDiscount ? (
                                                                        <span>
                                                                            Mínimo:{' '}
                                                                            {currencyFormat(
                                                                                quoted.minUnitPrice
                                                                            )}{' '}
                                                                            (
                                                                            {
                                                                                quoted.maxDiscount
                                                                            }
                                                                            % de desconto)
                                                                        </span>
                                                                    ) : (
                                                                        <span className="text-warning">
                                                                            Sem desconto
                                                                            autorizado
                                                                        </span>
                                                                    )}
                                                                    {quoted.stockEnabled && (
                                                                        <span>
                                                                            Estoque:{' '}
                                                                            {quoted.stockQuantity ??
                                                                                0}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </td>
                                                        <td>
                                                            <input
                                                                type="number"
                                                                min={1}
                                                                className="input input-bordered input-sm w-20"
                                                                value={line.quantity}
                                                                onChange={event =>
                                                                    updateLine(
                                                                        line.catalogId,
                                                                        {
                                                                            quantity:
                                                                                Math.max(
                                                                                    1,
                                                                                    parseInt(
                                                                                        event
                                                                                            .target
                                                                                            .value,
                                                                                        10
                                                                                    ) ||
                                                                                        1
                                                                                ),
                                                                        }
                                                                    )
                                                                }
                                                            />
                                                        </td>
                                                        <td>
                                                            <input
                                                                type="text"
                                                                className="input input-bordered input-sm w-36"
                                                                placeholder={
                                                                    quoted
                                                                        ? currencyFormat(
                                                                              quoted.basePrice
                                                                          )
                                                                        : 'Preço de tabela'
                                                                }
                                                                value={line.priceInput}
                                                                onChange={event =>
                                                                    updateLine(
                                                                        line.catalogId,
                                                                        {
                                                                            priceInput:
                                                                                event
                                                                                    .target
                                                                                    .value,
                                                                        }
                                                                    )
                                                                }
                                                            />
                                                        </td>
                                                        <td className="text-right font-medium">
                                                            {quoted
                                                                ? currencyFormat(
                                                                      quoted.lineTotal
                                                                  )
                                                                : '—'}
                                                        </td>
                                                        <td>
                                                            <button
                                                                className="btn btn-ghost btn-xs btn-circle text-error"
                                                                title="Remover"
                                                                onClick={() =>
                                                                    removeLine(
                                                                        line.catalogId
                                                                    )
                                                                }
                                                            >
                                                                <MdDelete size={16} />
                                                            </button>
                                                        </td>
                                                    </tr>
                                                )
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>

                        {quoteError && (
                            <div className="alert alert-error mt-4 text-sm">
                                <span>{quoteError}</span>
                            </div>
                        )}
                    </div>
                </div>

                {/* ===== Resumo e pagamento ===== */}
                <div className="flex flex-col gap-6">
                    <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6">
                        <h2 className="text-lg font-semibold text-base-content mb-4">
                            Cliente e pagamento
                        </h2>

                        <div className="flex flex-col gap-4">
                            <div className="form-control">
                                <ClientSelector
                                    value={clientId}
                                    onChange={selected => setClientId(selected)}
                                />
                            </div>

                            {/* O MESMO componente do agendamento: ele cota as formas do
                                gateway com a taxa já embutida e oferece a saída "decidir na
                                tela de pagamento", por onde passam dinheiro, maquininha e o
                                split. Um seletor próprio aqui seria uma segunda regra de
                                pagamento para manter — e foi o que fez a primeira versão
                                desta tela fechar em cartão sem gerar link nenhum. */}
                            <div className="form-control">
                                <label className="label pb-1">
                                    <span className="label-text font-medium">
                                        Data da venda
                                    </span>
                                </label>
                                <input
                                    type="date"
                                    className="input input-bordered w-full"
                                    value={saleDate}
                                    max={localTodayInput()}
                                    onChange={event => setSaleDate(event.target.value)}
                                />
                                <span className="text-xs text-base-content/60 mt-2">
                                    Vira o vencimento da cobrança. Recebido em dinheiro ou
                                    maquininha, é também a data do pagamento.
                                </span>
                            </div>

                            <div className="form-control">
                                <label className="label pb-1">
                                    <span className="label-text font-medium">
                                        Forma de pagamento
                                    </span>
                                </label>
                                <PaymentOptions
                                    amount={total}
                                    context="counter-sale"
                                    selectedMethod={paymentMethod || null}
                                    selectedInstallments={installmentCount}
                                    onSelect={option => {
                                        setPaymentMethod(option.method)
                                        setInstallmentCount(
                                            option.method === 'CREDIT_CARD' &&
                                                option.installments > 1
                                                ? option.installments
                                                : null
                                        )
                                    }}
                                    onQuoteChange={setPaymentQuote}
                                    onCustomize={() => {
                                        setPaymentMethod('')
                                        setInstallmentCount(null)
                                        setPaymentQuote(null)
                                    }}
                                />
                                <span className="text-xs text-base-content/60 mt-2">
                                    {paymentMethod
                                        ? 'A cobrança nasce pronta nesta forma; o estoque baixa quando o pagamento entrar.'
                                        : 'Sem forma escolhida, você segue para a cobrança — é lá que ficam dinheiro, maquininha e o pagamento dividido.'}
                                </span>
                            </div>

                            <div className="form-control">
                                <label className="label pb-1">
                                    <span className="label-text font-medium">
                                        Observações
                                    </span>
                                </label>
                                <textarea
                                    className="textarea textarea-bordered w-full"
                                    rows={2}
                                    placeholder="Opcional"
                                    value={notes}
                                    onChange={event => setNotes(event.target.value)}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6">
                        <h2 className="text-lg font-semibold text-base-content mb-4">
                            Resumo
                        </h2>

                        <div className="flex flex-col gap-2 text-sm">
                            <div className="flex justify-between">
                                <span className="text-base-content/70">
                                    Mercadoria
                                </span>
                                <span>{currencyFormat(subtotal)}</span>
                            </div>
                            {discount > 0 && (
                                <div className="flex justify-between text-success">
                                    <span>Desconto</span>
                                    <span>− {currencyFormat(discount)}</span>
                                </div>
                            )}
                            <div className="divider my-1" />
                            {/* O que o cliente paga é o total da FORMA escolhida (taxa
                                embutida), não a base. Sem forma, mostra a base e diz que
                                ela ainda pode mudar — anunciar a base como valor final é o
                                que fazia o link chegar com outro número. */}
                            <div className="flex justify-between text-lg font-bold">
                                <span>
                                    {paymentQuote
                                        ? `Total ${METHOD_TOTAL_LABEL[paymentQuote.method]}`
                                        : 'Total'}
                                </span>
                                <span>
                                    {quoting
                                        ? '...'
                                        : currencyFormat(
                                              paymentQuote?.total ?? total
                                          )}
                                </span>
                            </div>
                            {paymentQuote && paymentQuote.installments > 1 && (
                                <p className="text-xs text-base-content/60">
                                    {paymentQuote.installments}x de{' '}
                                    {currencyFormat(paymentQuote.installmentAmount)}
                                </p>
                            )}
                            <p className="text-xs text-base-content/50 mt-1">
                                {paymentQuote
                                    ? 'Valor já com a taxa da forma escolhida.'
                                    : 'A taxa da forma de pagamento, quando houver, entra por cima deste valor.'}
                            </p>
                        </div>

                        {missing.length > 0 && (
                            <div className="alert alert-info mt-4 text-sm">
                                <span>
                                    Para concluir, informe {missing.join(', ')}.
                                </span>
                            </div>
                        )}

                        <button
                            className="btn btn-primary w-full mt-4"
                            disabled={
                                selling ||
                                quoting ||
                                missing.length > 0 ||
                                total <= 0
                            }
                            onClick={handleSell}
                        >
                            {selling ? (
                                <span className="loading loading-spinner loading-sm" />
                            ) : (
                                <MdPointOfSale className="text-lg" />
                            )}
                            Concluir venda
                        </button>
                    </div>
                </div>
            </div>
        </div>
    )
}
